import {
  mockParentUsers,
  mockParentUsersSummary,
} from 'src/__mocks__/users/mockParentUsers';
import { mockUserDetailsList } from '../../__mocks__/users/userDetailListMock';
import { mockUserDetails } from '../../__mocks__/users/userDetailsMock';
import { createHttpService } from '../ApiService';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import { TPaginationParams } from '../__shared/shared.interfaces';
import {
  TGuestListApiResponse,
  TParentListApiResponse,
  TPrimaryDetails,
  TStaffEligibleResponse,
  TSupervisorsResponse,
  TUserDetails,
  TVisitorListApiResponse,
} from './users.interfaces';
import { ParentUser, VisitorUser, GuestUser, InternalUser, TInternalUserListApiResponse, UsersByCategoryParams, TUsersByCategoryResponse, UserByCategoryDto, UserByCategoryItem } from './users.api.interfaces';
import {
  mockVisitorUsers,
  mockVisitorUsersSummary,
} from 'src/__mocks__/users/mockVisitorUsers';
import {
  mockGuestUsers,
  mockGuestUsersSummary,
} from 'src/__mocks__/users/mockGuestUsers';
import { UsersSummary as LocalUsersSummary } from 'src/views/modules/users/users.interfaces';
import { VisitorsSummary } from './users.api.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useUsersService = () => {
  const httpAPI = createHttpService();
  const baseUserPath = '';
  const withMockData = isAppWithMock();

  const getUsers = (params: TPaginationParams) => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<TUserDetails>(
          {
            data: mockUserDetailsList,
            count: mockUserDetailsList.length,
            page: params.page ?? 1,
            limit: params.limit ?? 10,
          },
          'data'
        )
      );
    }
    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
      },
    };
    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUserPath}`, requestObj)
      .then((res) =>
        normalizePaginatedResponse<TUserDetails>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      );
  };
  const getUserById = (id: string) => {
    if (withMockData) {
      return Promise.resolve(mockUserDetails);
    }
    return httpAPI.users.get<TUserDetails>(`${baseUserPath}/${id}`);
  };

  const getParentList = (
    params: TPaginationParams
  ): Promise<TParentListApiResponse> => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<ParentUser>(
          {
            data: mockParentUsers,
            count: mockParentUsers.length,
            page: params.page ?? 1,
            limit: params.limit ?? 10,
          },
          'data'
        )
      );
    }
    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
      },
    };
    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUserPath}/parents`, requestObj)
      .then((res) =>
        normalizePaginatedResponse<ParentUser>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      );
  };

  const getVisitorList = (
    params: TPaginationParams
  ): Promise<TVisitorListApiResponse> => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<VisitorUser>(
          {
            data: mockVisitorUsers,
            count: mockVisitorUsers.length,
            page: params.page ?? 1,
            limit: params.limit ?? 10,
          },
          'data'
        )
      );
    }
    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
      },
    };
    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUserPath}/visitors`, requestObj)
      .then((res) =>
        normalizePaginatedResponse<VisitorUser>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      );
  };

  const getGuestList = (
    params: TPaginationParams
  ): Promise<TGuestListApiResponse> => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<GuestUser>(
          {
            data: mockGuestUsers,
            count: mockGuestUsers.length,
            page: params.page ?? 1,
            limit: params.limit ?? 10,
          },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 10,
      },
    };
    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUserPath}/guests`, requestObj)
      .then((res) =>
        normalizePaginatedResponse<GuestUser>(
          (res as Record<string, unknown>) ?? {},
          'data'
        )
      );
  };

  const getParentUsersSummary = (): Promise<LocalUsersSummary> => {
    if (withMockData) {
      return Promise.resolve(mockParentUsersSummary);
    }
    return httpAPI.users.get<LocalUsersSummary>(`${baseUserPath}/parents/summary`);
  };

  const getVisitorUsersSummary = (): Promise<VisitorsSummary> => {
    if (withMockData) {
      return Promise.resolve(mockVisitorUsersSummary);
    }
    return httpAPI.users.get<VisitorsSummary>(`${baseUserPath}/visitors/summary`);
  };

  const getGuestUsersSummary = (): Promise<LocalUsersSummary> => {
    if (withMockData) {
      return Promise.resolve(mockGuestUsersSummary);
    }
    return httpAPI.users.get<LocalUsersSummary>(`${baseUserPath}/guests/summary`);
  };

  const searchUsers = (query: string): Promise<{ data: TPrimaryDetails[] }> => {
    if (withMockData) {
      const q = query.trim().toLowerCase();
      if (!q) return Promise.resolve({ data: [] });
      const filtered = mockUserDetailsList.filter(
        (u) =>
          u.basicDetails.firstName.toLowerCase().includes(q) ||
          u.basicDetails.lastName.toLowerCase().includes(q) ||
          u.id?.toLowerCase().includes(q)
      );
      const data: TPrimaryDetails[] = filtered.slice(0, 10).map((u) => ({
        id: u.id,
        firstName: u.basicDetails.firstName,
        middleName: u.basicDetails.middleName,
        lastName: u.basicDetails.lastName,
        gender: u.basicDetails.gender,
        profile: u.basicDetails.profile,
      }));
      return Promise.resolve({ data });
    }
    return httpAPI.users
      .get<{ data: any[] }>(`${baseUserPath}/search`, { params: { q: query } })
      .then((res) => ({
        data: (res.data || []).map((u: any) => ({
          id: u._id,
          firstName: u.first_name,
          middleName: u.middle_name,
          lastName: u.last_name,
          gender: u.gender || 'select',
        })),
      })) as Promise<{ data: TPrimaryDetails[] }>;
  };

  const createUser = (user: TUserDetails) => {
    if (withMockData) {
      return Promise.resolve(mockUserDetails);
    }
    return httpAPI.users.post<TUserDetails>(`${baseUserPath}`, user);
  };

  const updateUser = (id: string, user: TUserDetails) => {
    if (withMockData) {
      return Promise.resolve(mockUserDetails);
    }
    return httpAPI.users.put<TUserDetails>(`${baseUserPath}/${id}`, user);
  };

  const getStaffEligibleUsers = (
    _instituteId: string,
    search?: string
  ): Promise<TStaffEligibleResponse> => {
    if (withMockData) {
      return Promise.resolve({ data: [] });
    }
    return httpAPI.users
      .get<TStaffEligibleResponse>(`${baseUserPath}/staff-eligible`, {
        params: { ...(search && { search }) },
      })
      .then((res: any) => (Array.isArray(res) ? { data: res } : res ?? { data: [] }));
  };

  const getSupervisors = (
    _instituteId: string
  ): Promise<TSupervisorsResponse> => {
    if (withMockData) {
      return Promise.resolve({ data: [] });
    }
    return httpAPI.users
      .get<TSupervisorsResponse>(`${baseUserPath}/supervisors`)
      .then((res: any) => (Array.isArray(res) ? { data: res } : res ?? { data: [] }));
  };

  const getInternalUsers = (
    params: TPaginationParams & { search?: string }
  ): Promise<TInternalUserListApiResponse> => {
    if (withMockData) {
      return Promise.resolve(
        normalizePaginatedResponse<InternalUser>(
          { data: [], count: 0, page: params.page ?? 1, limit: params.limit ?? 10 },
          'data'
        )
      );
    }
    return httpAPI.users
      .get<TInternalUserListApiResponse>(`${baseUserPath}/internal`, {
        params: {
          page: Number(params.page) || 1,
          limit: Number(params.limit) || 10,
          ...(params.search && { search: params.search }),
        },
      })
      .then((res: any) => {
        // res is the BE body: { data: [...], count: N, page: N, limit: N }
        return normalizePaginatedResponse<InternalUser>(res ?? {}, 'data');
      });
  };

  // Maps the snake_case `UserByCategoryDto` payload to the camelCase shape the
  // by-category table and detail view consume.
  const mapUserByCategoryDto = (dto: UserByCategoryDto): UserByCategoryItem => ({
    id: dto._id,
    firstName: dto.first_name,
    middleName: dto.middle_name,
    lastName: dto.last_name,
    gender: dto.gender,
    dateOfBirth: dto.date_of_birth,
    description: dto.description,
    userName: dto.user_name,
    email: dto.email,
    phone: dto.phone,
    userRole: dto.user_role,
    userStatus: dto.user_status ?? '',
    userCategory: dto.user_category,
    instituteId: dto.institute_id,
    profilePicture: dto.profile_picture_ref,
    createdBy: dto.created_by,
    updatedBy: dto.updated_by,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    lastActive: dto.last_active,
  });

  const getUsersByCategory = (
    params: UsersByCategoryParams
  ): Promise<TUsersByCategoryResponse> => {
    if (withMockData) {
      return Promise.resolve({ data: [], total: 0, totalPages: 0, page: params.page ?? 1, limit: params.limit ?? 10 });
    }
    return httpAPI.users
      .get<Record<string, unknown>>(`${baseUserPath}/by-category`, {
        params: {
          category: params.category,
          page: params.page ?? 1,
          limit: params.limit ?? 10,
          ...(params.search ? { search: params.search } : {}),
        },
      })
      .then((res) => {
        const normalized = normalizePaginatedResponse<UserByCategoryDto>(
          (res as Record<string, unknown>) ?? {},
          'data'
        );
        return { ...normalized, data: normalized.data.map(mapUserByCategoryDto) };
      });
  };

  return {
    getUsers,
    getUserById,
    searchUsers,
    getParentList,
    getVisitorList,
    getGuestList,
    getParentUsersSummary,
    getVisitorUsersSummary,
    getGuestUsersSummary,
    createUser,
    updateUser,
    getStaffEligibleUsers,
    getSupervisors,
    getInternalUsers,
    getUsersByCategory,
  };
};
